import { Button } from '@/components/atoms/Button';
import { ScrollArea } from '@/components/atoms/ScrollArea';
import Image from 'next/image';
import { ExtendedFilter } from '../../types/models/filter';
import { SelectedFilterOption } from '../atoms/SelectedFilterOption';
import { SelectedFilterTags } from './SelectedFilterTags';

interface AllFiltersProps {
  tempFilters: ExtendedFilter[];
  onClose: () => void;
  onChangeCheckedTempFilter: (filterId: string, optionName: string) => void;
  onClearTempFilters: () => void;
  onApplyTempFilters: () => void;
}

export function AllFiltersPanel({
  tempFilters,
  onClose,
  onChangeCheckedTempFilter,
  onClearTempFilters,
  onApplyTempFilters,
}: AllFiltersProps) {
  // 필터 순서 정의
  const filterOrder = ['meat_type', 'origin', 'storage_type', 'meat_loin'];

  // 필터 정렬 함수
  const sortFilters = (filters: ExtendedFilter[]) => {
    // 깊은 복사로 원본 배열 변경 방지
    const sortedFilters = [...filters];

    // 지정된 순서대로 정렬
    sortedFilters.sort((a, b) => {
      const indexA = filterOrder.indexOf(a.id);
      const indexB = filterOrder.indexOf(b.id);

      // 둘 다 지정된 순서에 있는 경우
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // a만 지정된 순서에 있는 경우
      if (indexA !== -1) {
        return -1;
      }

      // b만 지정된 순서에 있는 경우
      if (indexB !== -1) {
        return 1;
      }

      // 둘 다 지정된 순서에 없는 경우 원래 순서 유지
      return 0;
    });

    return sortedFilters;
  };

  // 정렬된 필터 목록
  const sortedFilters = sortFilters(tempFilters);

  return (
    <div className="w-[1580px] h-[703px] border border-[black] rounded-lg bg-white shadow-md relative overflow-hidden">
      {/* 닫기 버튼 */}
      <Button
        variant="ghost"
        className="w-fit h-fit z-10 absolute top-0 right-2"
        onClick={onClose}
      >
        <Image src={'/images/x.svg'} alt="close" width={24} height={24} />
      </Button>
      <div className="flex flex-col w-full h-full">
        {/* 필터 카테고리 및 옵션 목록 - 스크롤 영역 */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="flex flex-col">
              {sortedFilters.map((filter) => (
                <div
                  key={filter.id}
                  className="border border-(--secondary-200)"
                >
                  <div className="flex flex-row">
                    <div className="w-38 min-h-8 bg-[#F7F7FA] text-(--secondary-800) body-14sb px-4 py-2">
                      {filter.name}
                    </div>
                    <div className="grid grid-cols-11 gap-2 w-full pl-1 pr-20 py-0.5">
                      {filter.options.map((option) => {
                        // 텍스트 길이에 따라 칸 수 결정
                        const isLongText = option.name.length > 12;

                        return (
                          <SelectedFilterOption
                            key={option.name}
                            filterId={filter.id}
                            option={option}
                            isLongText={isLongText}
                            onChangeCheckedTempFilter={
                              onChangeCheckedTempFilter
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* 선택된 필터 태그 영역과 하단 버튼 영역을 포함하는 컨테이너 */}
        <div className="border-t flex flex-col min-h-36">
          {/* 선택된 필터 태그 영역 - 동적 높이 */}
          <div className="p-4">
            <SelectedFilterTags
              onChangeCheckedFilter={onChangeCheckedTempFilter}
            />
          </div>

          {/* 하단 버튼 영역 - 바닥에 고정 */}
          <div className="p-4 mt-auto">
            <div className="flex gap-6 justify-center">
              <Button
                variant="outline"
                onClick={onClearTempFilters}
                className="w-40 h-10 body-18sb border-(--secodary-700) py-2 px-6"
              >
                초기화
              </Button>
              <Button
                onClick={onApplyTempFilters}
                className="w-40 h-10 body-18sb bg-(--primary-400) py-2 px-6"
              >
                적용
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
